import { ArrowRight, Check, Info, Lock, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { APP_NAME } from '../../shared/brand';
import { browserTimeZone } from '../../shared/dates';
import { BUILTIN_BY_KEY } from '../../shared/nutrients';
import { ACTIVITY, maxPaceKg, minHealthyGoalKg, suggestTargets, type Activity, type Sex } from '../../shared/targets';
import { formatWeight, ftInToCm, fromDisplayWeight, type WeightUnit } from '../../shared/units';
import { useOnboard, useSession, useSettings } from '../api/hooks';
import { geminiTrainingNote, RECIPE_LINK_NOTE } from '../lib/privacy';
import { localToday } from '../lib/today';

const NUTRIENT_PICKS = ['fiber', 'sodium', 'added_sugar', 'sat_fat', 'potassium', 'calcium', 'iron', 'vitamin_d', 'vitamin_c', 'caffeine'];
const THIS_YEAR = new Date().getFullYear();

function num(s: string): number | null {
  const n = Number(s.replace(',', '.'));
  return s.trim() !== '' && Number.isFinite(n) ? n : null;
}

/** First-run setup for a personal account. Everything here is editable later in You. */
export function OnboardingPage() {
  const onboard = useOnboard();
  const settings = useSettings();
  // With sign-in on, this is a shared server: say where the log lives and ask people to acknowledge what goes to Gemini.
  const signInOn = useSession().data?.required ?? false;
  const paidTier = settings.data?.ai.paidTier ?? false;
  const [understood, setUnderstood] = useState(false);
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<WeightUnit>('lb');
  const [sex, setSex] = useState<Sex>('female');
  const [birthYear, setBirthYear] = useState('');
  const [ft, setFt] = useState('5');
  const [inch, setInch] = useState('8');
  const [cm, setCm] = useState('173');
  const [weight, setWeight] = useState('');
  const [activity, setActivity] = useState<Activity>('light');
  const [goal, setGoal] = useState('');
  const [pace, setPace] = useState(0.45359237);
  const [tracked, setTracked] = useState<Set<string>>(new Set(['fiber', 'sodium']));
  const [error, setError] = useState<string | null>(null);

  const heightCm = unit === 'lb' ? ftInToCm(num(ft) ?? 0, num(inch) ?? 0) : (num(cm) ?? 0);
  const currentKg = num(weight) !== null ? fromDisplayWeight(num(weight)!, unit) : null;
  const goalKg = num(goal) !== null ? fromDisplayWeight(num(goal)!, unit) : null;
  const year = num(birthYear);
  const validBasics = year !== null && year >= 1900 && year <= THIS_YEAR - 13 && heightCm >= 100 && heightCm <= 250 && currentKg !== null && currentKg >= 20 && currentKg <= 400;
  const healthyFloor = heightCm >= 100 ? minHealthyGoalKg(heightCm) : 0;
  const goalTooLow = goalKg !== null && goalKg < healthyFloor;
  const losing = goalKg !== null && currentKg !== null && goalKg < currentKg - 0.2;
  const cap = currentKg ? maxPaceKg(currentKg) : 1;
  const paceOptions = (unit === 'lb' ? [0.5, 1, 1.5, 2].map((lb) => lb * 0.45359237) : [0.25, 0.5, 0.75, 1]).filter((kg) => kg <= cap + 1e-9);
  const effectivePace = paceOptions.some((p) => Math.abs(p - pace) < 1e-6) ? pace : (paceOptions[paceOptions.length - 1] ?? 0);

  const preview = useMemo(() => {
    if (!validBasics || currentKg === null || goalTooLow) return null;
    return suggestTargets({ sex, birthYear: year!, heightCm, activity, currentKg, goalKg, paceKgPerWeek: goalKg !== null ? effectivePace : 0, unit });
  }, [validBasics, currentKg, goalTooLow, sex, year, heightCm, activity, goalKg, effectivePace, unit]);

  const submit = () => {
    setError(null);
    if (!validBasics || currentKg === null || year === null) return setError('Fill in birth year, height and current weight.');
    if (goalTooLow) return setError(`Goal weights below ${formatWeight(healthyFloor, unit, { decimals: 0 })} (BMI 18.5) aren't supported.`);
    onboard.mutate(
      {
        name: name.trim(),
        sex,
        birthYear: year,
        heightCm: Math.round(heightCm * 10) / 10,
        activity,
        weightUnit: unit,
        currentKg: Math.round(currentKg * 100) / 100,
        goalKg: goalKg === null ? null : Math.round(goalKg * 100) / 100,
        paceKgPerWeek: goalKg === null ? 0 : effectivePace,
        timezone: browserTimeZone(),
        tracked: [...tracked],
        today: localToday(),
      },
      { onError: (e) => setError(e.message) },
    );
  };

  return (
    <div style={{ minHeight: '100dvh', padding: '28px 16px 48px', display: 'flex', justifyContent: 'center' }}>
      <div className="stack" style={{ width: '100%', maxWidth: 600, gap: 18 }}>
        <div className="stack" style={{ gap: 8 }}>
          <span className="eyebrow eyebrow--ai">
            <Sparkles size={13} /> Welcome to {APP_NAME}
          </span>
          <h1 className="page__title">Let's set you up</h1>
          <p className="page__sub">About a minute. Everything here can be changed later in You.</p>
        </div>

        <section className="card card--pad stack" style={{ gap: 14 }}>
          <h2 className="section-title">About you</h2>
          <label className="field">
            <span className="field__label">What should we call you? (optional)</span>
            <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} data-autofocus />
          </label>
          <div className="grid-2">
            <div className="field">
              <span className="field__label">Units</span>
              <div className="seg seg--block" role="radiogroup" aria-label="Units">
                {(['lb', 'kg'] as const).map((u) => (
                  <button key={u} role="radio" aria-checked={unit === u} className={`seg__btn ${unit === u ? 'is-active' : ''}`} onClick={() => setUnit(u)}>
                    {u === 'lb' ? 'lb · ft' : 'kg · cm'}
                  </button>
                ))}
              </div>
            </div>
            <label className="field">
              <span className="field__label">Sex (for energy estimates)</span>
              <select className="select" value={sex} onChange={(e) => setSex(e.target.value as Sex)}>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other / prefer not to say</option>
              </select>
            </label>
          </div>
          <div className="grid-2">
            <label className="field">
              <span className="field__label">Birth year</span>
              <input className="input num" inputMode="numeric" placeholder="e.g. 1990" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} />
            </label>
            {unit === 'lb' ? (
              <div className="field">
                <span className="field__label">Height</span>
                <div className="row">
                  <div className="input-affix grow">
                    <input className="input num" inputMode="numeric" aria-label="Feet" value={ft} onChange={(e) => setFt(e.target.value)} />
                    <span className="input-affix__suffix">ft</span>
                  </div>
                  <div className="input-affix grow">
                    <input className="input num" inputMode="numeric" aria-label="Inches" value={inch} onChange={(e) => setInch(e.target.value)} />
                    <span className="input-affix__suffix">in</span>
                  </div>
                </div>
              </div>
            ) : (
              <label className="field">
                <span className="field__label">Height</span>
                <div className="input-affix">
                  <input className="input num" inputMode="decimal" value={cm} onChange={(e) => setCm(e.target.value)} />
                  <span className="input-affix__suffix">cm</span>
                </div>
              </label>
            )}
          </div>
          <div className="grid-2">
            <label className="field">
              <span className="field__label">Current weight</span>
              <div className="input-affix">
                <input className="input num" inputMode="decimal" placeholder={unit === 'lb' ? 'e.g. 185' : 'e.g. 84'} value={weight} onChange={(e) => setWeight(e.target.value)} />
                <span className="input-affix__suffix">{unit}</span>
              </div>
            </label>
            <label className="field">
              <span className="field__label">Activity</span>
              <select className="select" value={activity} onChange={(e) => setActivity(e.target.value as Activity)}>
                {Object.entries(ACTIVITY).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
              <span className="field__hint">{ACTIVITY[activity].hint}</span>
            </label>
          </div>
        </section>

        <section className="card card--pad stack" style={{ gap: 14 }}>
          <h2 className="section-title">Your goal</h2>
          <div className="grid-2">
            <label className="field">
              <span className="field__label">Goal weight (optional)</span>
              <div className="input-affix">
                <input className="input num" inputMode="decimal" placeholder="leave blank to maintain" value={goal} onChange={(e) => setGoal(e.target.value)} />
                <span className="input-affix__suffix">{unit}</span>
              </div>
              {goalTooLow && <span className="field__error">Below BMI 18.5 for your height — choose {formatWeight(healthyFloor, unit, { decimals: 0 })} or more.</span>}
            </label>
            {losing && (
              <label className="field">
                <span className="field__label">Pace</span>
                <select className="select" value={String(effectivePace)} onChange={(e) => setPace(Number(e.target.value))}>
                  {paceOptions.map((kg) => (
                    <option key={kg} value={String(kg)}>
                      {formatWeight(kg, unit, { decimals: unit === 'lb' ? 1 : 2 })} per week
                    </option>
                  ))}
                </select>
                <span className="field__hint">Capped at 1% of your weight per week.</span>
              </label>
            )}
          </div>
          {preview && (
            <div className="card card--soft stack" style={{ padding: 14, gap: 10 }}>
              <div className="eyebrow">Your starting daily targets</div>
              <div className="grid-3">
                {[
                  ['Calories', preview.kcal.toLocaleString('en-US'), 'kcal'],
                  ['Protein', String(preview.protein), 'g'],
                  ['Carbs · Fat', `${preview.carbs} · ${preview.fat}`, 'g'],
                ].map(([label, value, u]) => (
                  <div key={label}>
                    <div className="stat__label">{label}</div>
                    <div className="stat__value num" style={{ fontSize: 22 }}>
                      {value} <span style={{ fontSize: 13 }}>{u}</span>
                    </div>
                  </div>
                ))}
              </div>
              <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
                {preview.explanation.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="card card--pad stack" style={{ gap: 12 }}>
          <div>
            <h2 className="section-title">Track extra nutrients</h2>
            <p className="small muted" style={{ marginTop: 4 }}>
              Calories, protein, carbs and fat are always tracked. Add more now or anytime (including your own).
            </p>
          </div>
          <div className="row wrap">
            {NUTRIENT_PICKS.map((key) => {
              const on = tracked.has(key);
              return (
                <button
                  key={key}
                  className={`chip ${on ? 'is-active' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    setTracked((prev) => {
                      const next = new Set(prev);
                      if (on) next.delete(key);
                      else next.add(key);
                      return next;
                    })
                  }
                >
                  {BUILTIN_BY_KEY.get(key)?.name ?? key}
                </button>
              );
            })}
          </div>
        </section>

        <div className="banner banner--ai">
          <Lock size={17} />
          <div className="stack" style={{ gap: 12 }}>
            <span className="small">
              {signInOn ? (
                'Your log is stored on this server under your account. The person who runs it sees your name, emails, last visit and AI use, and can technically read anything stored here.'
              ) : (
                <>
                  Your log stays on this computer (<code>data/grams.db</code>).
                </>
              )}{' '}
              AI features send Google Gemini what each one needs: food descriptions and photos, food names and grams from your log, dated weigh-ins (and which one is your starting weight),
              profile basics, your Coach chats, and recipes you import (the ingredient list, pasted recipe text, a recipe page's text, title and site
              name, or a recipe photo or screenshot). Never your account's name or email, though text, pages and screenshots you import go as they
              are. {RECIPE_LINK_NOTE} In Chrome, Edge and Safari, voice input goes through the browser's speech
              service. In Brave and Firefox, a short clip is sent to Gemini and is not stored. A barcode photo is read on your device first, and
              sent to Gemini only if that fails. {geminiTrainingNote(paidTier)}
            </span>
            {signInOn && (
              <label className="consent">
                <input type="checkbox" className="sr-only" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
                <span className={`check ${understood ? 'is-on' : ''}`} aria-hidden="true">
                  <Check size={15} />
                </span>
                <span className="small" style={{ fontWeight: 600 }}>
                  I understand what's sent to Gemini
                </span>
              </label>
            )}
          </div>
        </div>
        {/* The importer runs on the server's command line, so it's only for local use. */}
        {!signInOn && (
          <div className="banner banner--ai">
            <Info size={17} />
            <span className="small">
              Coming from Redi? <code>pnpm import:redi</code> can bring over your profile, weigh-ins and food logs instead.
            </span>
          </div>
        )}

        {error && <p className="field__error">{error}</p>}
        <button className="btn btn--primary btn--lg btn--block" disabled={onboard.isPending || (signInOn && !understood)} onClick={submit}>
          Start tracking <ArrowRight size={18} />
        </button>
        {signInOn && !understood && <p className="small muted center">Tick “I understand what's sent to Gemini” above to start.</p>}
      </div>
    </div>
  );
}
